import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "motion/react"
import {
    canPromptPwaInstall,
    promptPwaInstall,
    subscribePwaInstall,
    isIosSafari,
    isStandalone,
} from "../lib/pwaInstall"

const BODY = "'Manrope', sans-serif"
const DISPLAY = "'Tilda Sans', sans-serif"
const MONO = "'JetBrains Mono', monospace"

export type PwaInstallBadgeProps = {
    compact?: boolean
}

/**
 * Кнопка «Установить PWA» в стиле бейджей магазинов.
 * Android/Chrome — системный диалог установки; iOS Safari — инструкция
 * «Поделиться → На экран „Домой"». В остальных случаях не рендерится.
 */
export default function PwaInstallBadge({ compact = false }: PwaInstallBadgeProps) {
    const [canPrompt, setCanPrompt] = useState(canPromptPwaInstall)
    const [showIosHint, setShowIosHint] = useState(false)

    useEffect(() => subscribePwaInstall(() => setCanPrompt(canPromptPwaInstall())), [])

    if (isStandalone()) return null
    const ios = isIosSafari()
    if (!canPrompt && !ios) return null

    const onClick = () => {
        if (canPrompt) {
            void promptPwaInstall()
        } else {
            setShowIosHint(true)
        }
    }

    const buttonClass = compact
        ? "flex cursor-pointer items-center gap-2.5 rounded-xl border border-white/12 bg-white/[0.04] px-3.5 py-2 hover:bg-white/[0.09] hover:border-white/30 transition-all lg:px-5"
        : "flex cursor-pointer items-center gap-3 rounded-2xl border border-white/20 bg-white/[0.06] px-5 py-3 hover:bg-white/[0.12] hover:border-white/40 transition-all"
    const iconSize = compact ? 26 : 34

    return (
        <>
            <button type="button" onClick={onClick} className={buttonClass}>
                <img
                    src="/icons/icon-192.png"
                    alt=""
                    width={iconSize}
                    height={iconSize}
                    className="shrink-0 rounded-[8px]"
                    style={{ width: iconSize, height: iconSize }}
                />
                <div className="text-left">
                    <div
                        className={compact ? "text-[9px] text-white/45 lg:text-[10px]" : "text-[10px] text-white/50"}
                        style={{ fontFamily: MONO, fontWeight: 600 }}>
                        Установить
                    </div>
                    <div
                        className={compact ? "text-[12px] text-white font-bold lg:text-[14px]" : "text-[14px] text-white font-bold"}
                        style={{ fontFamily: DISPLAY }}>
                        Веб-версию
                    </div>
                </div>
            </button>

            {createPortal(
                <AnimatePresence>
                    {showIosHint && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-[110] flex cursor-pointer items-end justify-center px-3 pb-[max(12px,var(--safe-bottom))] md:items-center"
                            style={{ background: "rgba(10,18,42,0.6)", backdropFilter: "blur(4px)" }}
                            onClick={() => setShowIosHint(false)}>
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 24 }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                className="w-full max-w-[420px] cursor-default rounded-[20px] p-4 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
                                style={{
                                    background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)",
                                    border: "1.5px solid rgba(255,255,255,0.12)",
                                }}
                                role="dialog"
                                aria-label="Установка приложения"
                                onClick={e => e.stopPropagation()}>
                                <button
                                    type="button"
                                    onClick={() => setShowIosHint(false)}
                                    aria-label="Закрыть"
                                    className="absolute top-2 right-2 flex size-7 cursor-pointer items-center justify-center rounded-full text-white/45 transition-colors hover:text-white">
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                                        <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                </button>

                                <div className="flex items-start gap-3 pr-6">
                                    <img src="/icons/icon-192.png" alt="" className="size-11 shrink-0 rounded-[12px]" />
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[14px] text-white" style={{ fontFamily: DISPLAY, fontWeight: 700 }}>
                                            Установите приложение
                                        </p>
                                        <p
                                            className="text-[13px] leading-relaxed text-white/70"
                                            style={{ fontFamily: BODY, fontWeight: 500 }}>
                                            Нажмите{" "}
                                            <svg
                                                width="14"
                                                height="17"
                                                viewBox="0 0 16 20"
                                                fill="none"
                                                aria-label="Поделиться"
                                                className="inline-block align-[-3px]">
                                                <path
                                                    d="M8 1v12M8 1L4.5 4.5M8 1l3.5 3.5M2 8v9a1 1 0 001 1h10a1 1 0 001-1V8"
                                                    stroke="#349be6"
                                                    strokeWidth="1.6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>{" "}
                                            в панели Safari, затем «На экран „Домой"»
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body,
            )}
        </>
    )
}
